import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { estimateRPM, fmtMoney } from "@/lib/earnings";

function daysSince(iso) {
  if (!iso) return 0;
  return Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/**
 * Inline-clickable run-rate value with a popover that breaks down the math
 * for a single breakout video.
 */
export function VideoRunRatePopover({ video, monetizationScore, label, eligible }) {
  const rpm = estimateRPM(monetizationScore);
  const days = daysSince(video.published_at);
  const lifetime = Math.round((video.views / 1000) * rpm);
  const monthly = Math.round(lifetime * (30 / days));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`run-rate-popover-${video.video_id}`}
          onClick={(e) => e.stopPropagation()}
          className={`font-mono text-[11px] underline decoration-dotted underline-offset-4 hover:decoration-solid focus:outline-none focus:ring-1 focus:ring-yellow-400/40 rounded-sm ${
            eligible ? "text-yellow-400" : "text-zinc-500"
          }`}
          title="Click for calculation"
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 bg-zinc-950 border-zinc-700 text-zinc-100 rounded-sm shadow-2xl font-mono text-xs p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-yellow-400/10 border-b border-zinc-800 px-4 py-2 uppercase tracking-[0.2em] text-yellow-400 text-[10px]">
          // Run-rate calculation
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-zinc-500 mb-1">Inputs</div>
            <Row label="Views" value={video.views.toLocaleString()} />
            <Row label="Posted" value={`${days} day${days === 1 ? "" : "s"} ago`} />
            <Row label="Assumed RPM" value={`$${rpm} / 1,000 views`} />
            <Row
              label="Status"
              value={eligible ? "Monetized (≥1k subs)" : "Pre-YPP (<1k subs)"}
              valueClass={eligible ? "text-green-400" : "text-zinc-500"}
            />
          </div>
          <div className="border-t border-zinc-800 pt-3">
            <div className="text-zinc-500 mb-1">Calculation</div>
            <div className="text-zinc-300 leading-relaxed">
              <div>
                lifetime ={" "}
                <span className="text-white">
                  {video.views.toLocaleString()} ÷ 1,000 × ${rpm}
                </span>
              </div>
              <div className="text-yellow-400 mt-0.5">
                ={" "}
                <span className="font-bold">
                  {fmtMoney(lifetime)}
                </span>
              </div>
              <div className="mt-2">
                run-rate ={" "}
                <span className="text-white">
                  {fmtMoney(lifetime)} × (30 ÷ {days})
                </span>
              </div>
              <div className="text-yellow-400 mt-0.5">
                ={" "}
                <span className="font-bold">{fmtMoney(monthly)} / mo</span>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 border-t border-zinc-800 pt-3 leading-relaxed">
            If this video keeps earning at its current pace for a full 30
            days, it would generate the run-rate figure. Real YouTube views
            usually taper after the first 30 days, so this is an upper-bound
            snapshot, not a guaranteed monthly payout.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Inline-clickable run-rate value with a popover that explains the niche-level
 * aggregate run-rate (sum of all breakout videos in this niche).
 */
export function NicheRunRatePopover({
  niche,
  total,
  lifetime,
  rpm,
  children,
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="niche-run-rate-popover"
          className="text-left underline decoration-dotted underline-offset-4 hover:decoration-solid focus:outline-none focus:ring-1 focus:ring-yellow-400/40 rounded-sm"
          title="Click for calculation"
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-96 bg-zinc-950 border-zinc-700 text-zinc-100 rounded-sm shadow-2xl font-mono text-xs p-0 overflow-hidden"
      >
        <div className="bg-yellow-400/10 border-b border-zinc-800 px-4 py-2 uppercase tracking-[0.2em] text-yellow-400 text-[10px]">
          // Niche run-rate breakdown
        </div>
        <div className="p-4 space-y-3">
          <div className="text-zinc-300 leading-relaxed">
            Total of <span className="text-white">{niche.videos.length}</span>{" "}
            breakout videos, each scaled to a 30-day pace using their own
            published date.
          </div>
          <div className="border-t border-zinc-800 pt-3">
            <div className="text-zinc-500 mb-1">Per video</div>
            <div className="space-y-1 max-h-44 overflow-y-auto">
              {niche.videos.map((v) => {
                const d = daysSince(v.published_at);
                const life = Math.round((v.views / 1000) * rpm);
                const mo = Math.round(life * (30 / d));
                return (
                  <div
                    key={v.video_id}
                    className="flex items-baseline justify-between gap-2"
                  >
                    <span className="truncate text-zinc-300 max-w-[55%]">
                      {v.channel_name}
                    </span>
                    <span className="text-zinc-500 text-[10px]">
                      {d}d · {(v.views / 1000).toFixed(0)}K
                    </span>
                    <span className="text-yellow-400 font-bold">
                      {fmtMoney(mo)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-3 space-y-1">
            <Row label="Assumed RPM" value={`$${rpm} / 1,000 views`} />
            <Row label="Total lifetime" value={fmtMoney(lifetime)} />
            <Row
              label="Total run-rate"
              value={`${fmtMoney(total)} / mo`}
              valueClass="text-yellow-400 font-bold"
            />
          </div>
          <p className="text-[11px] text-zinc-500 border-t border-zinc-800 pt-3 leading-relaxed">
            Assumed RPM comes from this niche's monetization-potential score
            ({niche.scores.monetization_potential}/10). Channels with under
            1,000 subs are pre-monetization — their figures are potential, not
            actual earnings yet.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ label, value, valueClass = "text-white" }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-zinc-500">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}
